/**
 * RegionalSettingsContext — runtime locale config from the centralized
 * `regional_settings` DB row (saved during onboarding Setup Wizard step 3
 * or Admin → Regional Settings).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import i18n from '../i18n';
import { getPublicRegionalSettings } from '../api/regionalSettings';
import { normalizeRegionalSettings } from '../config/regionalPresets';
import {
  syncRegionalFormatSettings,
  formatDate as formatDateValue,
  formatDateTime as formatDateTimeValue,
  formatTime as formatTimeValue,
  usesHour12 as usesHour12Value,
} from '../utils/regionalFormat';
import { formatCurrency, syncRegionalCurrency } from '../utils/currency';
import { syncRegionalDefaults, getRegionalDefaults } from '../utils/regionalDefaults';
import { syncAcademyUiLanguages } from '../utils/uiLanguage';

const RegionalSettingsContext = createContext(null);

export function RegionalSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => normalizeRegionalSettings({}));
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getPublicRegionalSettings();
      const normalized = normalizeRegionalSettings(data);
      setSettings(normalized);
      syncAcademyUiLanguages(normalized);
      syncRegionalFormatSettings(normalized);
      syncRegionalDefaults(normalized);
      syncRegionalCurrency(normalized);
    } catch {
      const fallback = normalizeRegionalSettings({});
      setSettings(fallback);
      syncAcademyUiLanguages(fallback);
      syncRegionalFormatSettings(fallback);
      syncRegionalDefaults(fallback);
      syncRegionalCurrency(fallback);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <RegionalSettingsContext.Provider value={{ settings, ready, refresh: load }}>
      {children}
    </RegionalSettingsContext.Provider>
  );
}

export function useRegionalSettings() {
  return useContext(RegionalSettingsContext);
}

export function useCurrencyFormatter() {
  return useCallback((amount) => formatCurrency(amount), []);
}

export function useDateFormatter() {
  return useCallback((value) => formatDateValue(value), []);
}

export function useDateTimeFormatter() {
  return useCallback((value, opts) => formatDateTimeValue(value, opts), []);
}

export function useTimeFormatter() {
  return useCallback((value) => formatTimeValue(value), []);
}

export function useHour12() {
  const { settings } = useRegionalSettings();
  return settings?.timeFormat === 'HOUR_12' || usesHour12Value();
}

/** Phone/address placeholders and default country from regional settings. */
export function useRegionalFormDefaults() {
  const { settings } = useRegionalSettings();
  const defaults = getRegionalDefaults();
  return {
    ...defaults,
    // Prefer live Regional Settings over module cache so phone dial ISO is correct on first paint after load.
    countryCode: settings?.countryCode || defaults.countryCode,
    countryName: settings?.countryName || defaults.countryName,
    phoneDialCode: settings?.phoneDialCode || defaults.phoneDialCode,
    phoneFormatHint: settings?.phoneFormatHint || defaults.phoneFormatHint,
  };
}

/** Country-aware payment defaults from regional_settings API. */
export function useRegionalPaymentDefaults() {
  const { settings } = useRegionalSettings();
  const cc = String(settings?.countryCode || '').trim().toUpperCase();
  const placeholderKey = cc ? `regional.paymentReference.${cc}` : 'regional.paymentReference.default';
  const routingKey = cc ? `regional.bankRouting.${cc}` : 'regional.bankRouting.default';
  return {
    defaultPaymentMethod: settings?.defaultPaymentMethod || 'CASH',
    // Always follow Header UI language — API placeholders are English catalog text.
    paymentReferencePlaceholder: i18n.t(placeholderKey, {
      defaultValue: i18n.t('regional.paymentReference.default'),
    }),
    bankRoutingLabel: i18n.t(routingKey, {
      defaultValue: settings?.bankRoutingLabel || i18n.t('regional.bankRouting.default'),
    }),
    taxLabel: settings?.taxLabel || '',
    taxIdLabel: settings?.taxIdLabel || '',
    taxRegistrationNumber: settings?.taxRegistrationNumber || '',
    showTaxFields: Boolean(settings?.taxIdLabel && String(settings.taxIdLabel).trim()),
  };
}
